import React from "react";
import { Link as ScrollLink, animateScroll as scroll } from "react-scroll";
import { NavLink } from "react-router-dom";
import "./Header.scss";
import { useState, useEffect, useRef } from "react";
import {
  fetchUser,
  logoutUser,
  logoutAllUsers,
  deleteAccount,
} from "../../context/ProfileContext";
import SymptomSearch from "../Search/Symptom";
import MedicineSearch from "../Search/Medicine";
import {
  FaShoppingCart,
  FaTimes,
  FaBars,
  FaBoxOpen,
  FaPen,
  FaTrash,
} from "react-icons/fa";
import { useCart } from "../../context/CartContext";
import { useSearchToggle } from "../../context/SearchToggleContext"; 

const Header = () => {
  const [click, setClick] = useState(false);
  const [user, setUser] = useState(null);
  const [color, setColor] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const cardRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const { isSearchVisible, setIsSearchVisible } = useSearchToggle();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ name: "", age: "", avatar: null });
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { cart } = useCart();

  const totalItems = cart?.reduce((acc, item) => acc + item.quantity, 0) || 0;

  useEffect(() => {
    if (user) {
      setFormData({ name: user.name || "", age: user.age || "", avatar: null });
    }
  }, [user]);

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, avatar: file });
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    const hasChanges =
      formData.name !== user.name ||
      formData.age !== user.age ||
      formData.avatar;
    if (!hasChanges) {
      return;
    }
    const form = new FormData();
    form.append("name", formData.name);
    form.append("age", formData.age);
    if (formData.avatar) {
      form.append("avatar", formData.avatar);
    }

    try {
      setIsLoading(true);
      const res = await fetch("/users/me", {
        method: "PATCH",
        credentials: "include",
        body: form,
      });
      if (res.ok) {
        const updatedUser = await res.json();
        setIsLoading(false);
        setError(null);
        setUser(updatedUser);
        setIsEditing(false);
        setPreview(null);
      } else {
        console.error("Update failed");
      }
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const getUser = async () => {
      try {
        if (!isMounted) return;
        const userData = await fetchUser();
        if (userData) {
          setUser(userData);
        } else {
          console.log("Session expired. Redirecting to login.");
        }
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    };

    getUser();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleDeleteAccount = async () => {
    try {
      const confirmDelete = window.confirm(
        "Are you sure you want to delete your account?"
      );
      if (confirmDelete) {
        await deleteAccount();
        setUser(null);
        window.location.reload();
      }
    } catch (error) {
      console.error("Error deleting account:", error);
    }
  };

  const handleLogout = async () => {
    try {
      await logoutUser();
      setUser(null);
      window.location.reload();
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const handleLogoutAll = async () => {
    try {
      await logoutAllUsers();
      setUser(null);
      window.location.reload();
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cardRef.current && !cardRef.current.contains(event.target)) {
        setShowCard(false);
        setIsEditing(false);
      }
    };

    const handleScroll = () => {
      setShowCard(false);
      setIsEditing(false);
    };

    document.addEventListener("click", handleClickOutside);
    window.addEventListener("scroll", handleScroll);

    return () => {
      document.removeEventListener("click", handleClickOutside);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(
        /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
          window.innerWidth <= 768
      );
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleClick = () => {
    setClick(!click);
  };

  const scrollToTop = () => {
    scroll.scrollToTop();
  };

  const changeColor = () => {
    if (window.scrollY >= 100) {
      setColor(true);
    } else {
      setColor(false);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", changeColor);
    return () => window.removeEventListener("scroll", changeColor);
  }, []);

  const toggleCard = (e) => {
    e.stopPropagation();
    setShowCard(!showCard);
  };

  return (
    <div className={color ? "Header Header-bg" : "Header"}>
      <div className="header-logo">
        <ScrollLink
          to="homepage"
          smooth={true}
          duration={500}
          onClick={scrollToTop}
        >
          <img src="/logo.png" alt="Logo" />
        </ScrollLink>
      </div>

      <div className="toggle-container">
        <label className="switch">
          <input
            type="checkbox"
            checked={isSearchVisible}
            onChange={() => setIsSearchVisible((prev) => !prev)}
          />
          <span className="slider round"></span>
        </label>
        {isSearchVisible ? (
          <p className="message">Searching for medicines</p>
        ) : (
          <p className="message">Searching for symptoms</p>
        )}
      </div>

      <div key={isSearchVisible ? "medicine" : "symptom"}>
        {isSearchVisible ? <MedicineSearch /> : <SymptomSearch />}
      </div>

      <div className={click ? "navigation active" : "navigation"}>
        <header>
          <nav>
            <ul>
              <li>
                <span onClick={() => handleClick()}>
                  {isMobile ? (
                    <NavLink
                      to={"/cart"}
                      className={({ isActive }) =>
                        isActive ? " nav-item activate" : "nav-item"
                      }
                    >
                      Cart
                    </NavLink>
                  ) : (
                    <NavLink
                      to={"/cart"}
                      className={({ isActive }) =>
                        isActive ? " nav-item activate" : "nav-item"
                      }
                    >
                      <FaShoppingCart title="Cart" />
                      {totalItems > 0 && (
                        <span className="cart-count">{totalItems}</span>
                      )}
                    </NavLink>
                  )}
                </span>
              </li>
              <li>
                <span onClick={() => handleClick()}>
                  {isMobile ? (
                    <NavLink
                      to={"/orders"}
                      className={({ isActive }) =>
                        isActive ? " nav-item activate" : "nav-item"
                      }
                    >
                      Orders
                    </NavLink>
                  ) : (
                    <NavLink
                      to={"/orders"}
                      className={({ isActive }) =>
                        isActive ? " nav-item activate" : "nav-item"
                      }
                    >
                      <FaBoxOpen title="Orders" />
                    </NavLink>
                  )}
                </span>
              </li>
              <li>
                <NavLink
                  to={"/"}
                  className={({ isActive }) =>
                    isActive ? " nav-item activate" : "nav-item"
                  }
                >
                  <span onClick={() => handleClick()}>Home</span>
                </NavLink>
              </li>
              <li>
                <NavLink
                  to={"/about"}
                  className={({ isActive }) =>
                    isActive ? " nav-item activate" : "nav-item"
                  }
                >
                  <span onClick={() => handleClick()}>About</span>
                </NavLink>
              </li>
              <li>
                <NavLink
                  to={"/blogs"}
                  className={({ isActive }) =>
                    isActive ? " nav-item activate" : "nav-item"
                  }
                >
                  <span onClick={() => handleClick()}>Blog</span>
                </NavLink>
              </li>
              <li>
                <NavLink
                  to={"/help"}
                  className={({ isActive }) =>
                    isActive ? " nav-item activate" : "nav-item"
                  }
                >
                  <span onClick={() => handleClick()}>Help</span>
                </NavLink>
              </li>

              {user ? (
                <li className="profile-menu">
                  <span className="profile" onClick={toggleCard}>
                    {isMobile ? (
                      <h3>Profile</h3>
                    ) : (
                      <img
                        src={
                          user.avatarUrl ||
                          `data:image/png;base64,${user.avatar}`
                        }
                        alt="Profile"
                      />
                    )}
                  </span>

                  {error && <div className="error-message">{error}</div>}
                  {isLoading && <div className="loader"></div>}
                  {showCard && (
                    <div className="profile-card" ref={cardRef}>
                      {!isEditing ? (
                        <>
                          <div className="profile-header">
                            {!user.isGoogleAccount && (
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setIsEditing(true);
                                }}
                              >
                                <FaPen />
                              </div>
                            )}
                            <FaTrash onClick={() => handleDeleteAccount()} />
                          </div>
                          {user.avatarUrl ? (
                            <img src={user.avatarUrl} alt="Profile" />
                          ) : (
                            <img
                              src={`data:image/png;base64,${user.avatar}`}
                              alt="Profile"
                            />
                          )}

                          <h3>{user.name}</h3>
                          <p>{user.email}</p>
                          <div className="profile-actions">
                            {" "}
                            <button
                              onClick={handleLogout}
                              className="logout-btn"
                            >
                              Logout
                            </button>
                            <button
                              onClick={handleLogoutAll}
                              className="logout-btn"
                            >
                              Logout All Devices
                            </button>
                          </div>
                        </>
                      ) : (
                        <form
                          className="edit-container"
                          onSubmit={handleUpdateProfile}
                        >
                          {preview ? (
                            <img src={preview} alt="Preview" />
                          ) : user.avatarUrl ? (
                            <img src={user.avatarUrl} alt="Profile" />
                          ) : (
                            <img
                              src={`data:image/png;base64,${user.avatar}`}
                              alt="Profile"
                            />
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleAvatarChange}
                          />
                          <input
                            type="text"
                            value={formData.name}
                            onChange={(e) =>
                              setFormData({ ...formData, name: e.target.value })
                            }
                            placeholder="Name"
                          />
                          <input
                            type="number"
                            value={formData.age}
                            onChange={(e) =>
                              setFormData({ ...formData, age: e.target.value })
                            }
                            placeholder="Age"
                          />
                          <div className="profile-actions">
                            <button type="submit" className="save-edit">
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => setIsEditing(false)}
                              className="cancel-edit"
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      )}
                    </div>
                  )}
                </li>
              ) : (
                <li>
                  <NavLink
                    to="/login"
                    className={({ isActive }) =>
                      isActive ? " nav-item activate" : "nav-item"
                    }
                    onClick={handleClick}
                  >
                    Login
                  </NavLink>
                </li>
              )}
            </ul>
          </nav>
        </header>
      </div>
      <div className="sidebar" onClick={handleClick}>
        {click ? (
          <FaTimes size={20} style={{ color: "#7b4e3c" }} />
        ) : (
          <FaBars size={20} style={{ color: "#7b4e3c" }} />
        )}
      </div>
    </div>
  );
};

export default Header;
